import Modal from "@/components/base/Modal";
import { formatDateTime, formatMoney } from "@/lib/format";
import type { CartItem } from "@/pages/pos/useCart";
import type { HeldSale } from "@/types";

interface HeldSalesModalProps {
  open: boolean;
  onClose: () => void;
  heldSales: HeldSale[];
  loading: boolean;
  onResume: (held: HeldSale) => void;
  onDelete: (held: HeldSale) => void;
}

function cartItems(held: HeldSale): CartItem[] {
  const raw = held.cart;
  if (Array.isArray(raw)) return raw as CartItem[];
  if (raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items)) {
    return (raw as { items: CartItem[] }).items;
  }
  return [];
}

function cartTotal(items: CartItem[]): number {
  return items.reduce(
    (sum, item) => sum + Math.max(item.quantity * item.unit_price - item.discount, 0),
    0,
  );
}

export default function HeldSalesModal({
  open,
  onClose,
  heldSales,
  loading,
  onResume,
  onDelete,
}: HeldSalesModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ventas suspendidas"
      subtitle="Selecciona una venta guardada para retomar el carrito."
      size="md"
    >
      {loading ? (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando ventas suspendidas...
        </div>
      ) : heldSales.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-pause-circle-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">No hay ventas suspendidas</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Usa "Suspender" en el carrito para guardar una venta a medias y retomarla después.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {heldSales.map((held) => {
            const items = cartItems(held);
            const total = cartTotal(items);
            return (
              <li
                key={held.id}
                className="flex flex-col gap-3 rounded-md border border-background-200 bg-background-100 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground-900">
                    {held.label || "Venta suspendida"}
                  </p>
                  <p className="mt-0.5 text-[11px] text-foreground-500">
                    {items.length} línea(s) · {formatMoney(total)} · {formatDateTime(held.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onResume(held)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600"
                  >
                    <i className="ri-play-circle-line text-base"></i>
                    Recuperar
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(held)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-500 hover:bg-background-200 hover:text-primary-600"
                  >
                    <i className="ri-delete-bin-line text-base"></i>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}